import { useMemo } from 'react';
import { useDynamicPermissions } from './useDynamicPermissions';
import { useAuth } from '../contexts/AuthContext';

interface Ticket {
  canEdit?: boolean;
  canEditAssignment?: boolean;
  canChangeState?: boolean;
  canAddNotes?: boolean;
  canAddAttachments?: boolean;
  canAddRelated?: boolean;
  editAccessReason?: string;
  state?: string;
  changeState?: string;
  problemState?: string;
  assignedToId?: string;
  createdById?: string;
  callerId?: string;
  [key: string]: any;
}

interface TicketPermissions {
  canView: boolean;
  canEdit: boolean;
  canEditAssignment: boolean;
  isAssignmentOnlyEditMode: boolean;
  canDelete: boolean;
  canChangeState: boolean;
  canAddWorkNote: boolean;
  canAddAttachments: boolean;
  canAddRelated: boolean;
  canPerformActions: boolean; // For actions that create new records (e.g., duplicate, create service request) - not state-restricted
  editAccessReason?: string;
}

/**
 * Hook to determine ticket-level permissions based on backend access control
 * and frontend permission checks.
 *
 * @param ticket - The ticket object (case, incident, problem, change, etc.)
 * @param resource - The resource name for permission checking (e.g., 'cases', 'incidents')
 * @returns Object with boolean flags for various ticket operations
 */
export function useTicketPermissions(
  ticket: Ticket | null | undefined,
  resource: string
): TicketPermissions {
  const { can } = useDynamicPermissions();
  const { user, isContact } = useAuth();

  return useMemo(() => {
    // DEBUG: Log entry point
    console.log(`[TICKET PERMISSIONS DEBUG] ========================================`);
    console.log(`[TICKET PERMISSIONS DEBUG] Resource: ${resource}`);
    console.log(`[TICKET PERMISSIONS DEBUG] User ID: ${user?.id}`);
    console.log(`[TICKET PERMISSIONS DEBUG] User Role: ${user?.role}`);
    console.log(`[TICKET PERMISSIONS DEBUG] Ticket ID: ${ticket?.id}`);
    console.log(`[TICKET PERMISSIONS DEBUG] Ticket assignedToId: ${ticket?.assignedToId}`);
    console.log(`[TICKET PERMISSIONS DEBUG] Ticket state: ${ticket?.state}`);
    console.log(`[TICKET PERMISSIONS DEBUG] Ticket changeState: ${ticket?.changeState}`);
    console.log(`[TICKET PERMISSIONS DEBUG] Backend canAddAttachments: ${ticket?.canAddAttachments}`);

    // Default: no permissions
    if (!ticket || !user) {
      console.log(`[TICKET PERMISSIONS DEBUG] Result: No ticket or user - returning defaults`);
      return {
        canView: false,
        canEdit: false,
        canEditAssignment: false,
        isAssignmentOnlyEditMode: false,
        canDelete: false,
        canChangeState: false,
        canAddWorkNote: false,
        canAddAttachments: false,
        canAddRelated: false,
        canPerformActions: false
      };
    }

    // Contact users have limited permissions
    // They can add notes and attachments to tickets that are not resolved/closed/cancelled
    if (isContact()) {
      const isReadOnly = isTicketDataReadOnly(ticket);
      return {
        canView: true, // If they can see the ticket, they can view it
        canEdit: false, // Contact users cannot edit tickets
        canEditAssignment: false,
        isAssignmentOnlyEditMode: false,
        canDelete: false,
        canChangeState: false,
        canAddWorkNote: !isReadOnly, // Can add notes if ticket is not resolved/closed/cancelled
        canAddAttachments: !isReadOnly, // Can add attachments if ticket is not resolved/closed/cancelled
        canAddRelated: false,
        canPerformActions: false // Contact users cannot perform actions
      };
    }

    // Check if backend provided granular permissions (from RBAC access control)
    const backendCanEdit = ticket.canEdit ?? null;
    const backendCanEditAssignment = ticket.canEditAssignment ?? null;
    const backendCanChangeState = ticket.canChangeState ?? null;
    const backendCanAddNotes = ticket.canAddNotes ?? null;
    const backendCanAddAttachments = ticket.canAddAttachments ?? null;
    const backendCanAddRelated = ticket.canAddRelated ?? null;
    const editAccessReason = ticket.editAccessReason;

    // Base permissions from RBAC system
    const hasUpdatePermission = can(resource, 'update');
    const hasDeletePermission = can(resource, 'delete');

    // Check if ticket data is read-only (resolved/closed/cancelled)
    const isDataReadOnly = isTicketDataReadOnly(ticket);
    console.log(`[TICKET PERMISSIONS DEBUG] isDataReadOnly: ${isDataReadOnly}`);

    // Check if ticket is fully locked (closed/cancelled only)
    const isFullyLocked = isTicketFullyLocked(ticket);
    console.log(`[TICKET PERMISSIONS DEBUG] isFullyLocked: ${isFullyLocked}`);

    // Check if user has privileged role (admin, manager, super_admin)
    // Privileged roles bypass assignment-based restrictions
    const userRole = user?.role?.toLowerCase();
    const isPrivilegedRole = userRole === 'admin' || userRole === 'manager' || userRole === 'super_admin';
    console.log(`[TICKET PERMISSIONS DEBUG] userRole (lowercase): ${userRole}`);
    console.log(`[TICKET PERMISSIONS DEBUG] isPrivilegedRole: ${isPrivilegedRole}`);

    // Check if ticket is assigned to the logged-in user
    const isAssignedToUser = ticket.assignedToId === user?.id;
    console.log(`[TICKET PERMISSIONS DEBUG] ===== ASSIGNMENT COMPARISON =====`);
    console.log(`[TICKET PERMISSIONS DEBUG] ticket.assignedToId: '${ticket.assignedToId}' (type: ${typeof ticket.assignedToId})`);
    console.log(`[TICKET PERMISSIONS DEBUG] user.id: '${user?.id}' (type: ${typeof user?.id})`);
    console.log(`[TICKET PERMISSIONS DEBUG] isAssignedToUser: ${isAssignedToUser}`);

    // Determine canEdit based on multiple factors
    let canEdit = false;
    if (backendCanEdit !== null) {
      // If backend explicitly provided canEdit, trust it
      // Backend has already checked all permissions (assigned to, group membership, role, etc.)
      // Block editing for resolved/closed/cancelled tickets
      canEdit = backendCanEdit && !isDataReadOnly;
    } else {
      // Fallback: if backend didn't provide canEdit, use permission-only check
      canEdit = hasUpdatePermission && !isDataReadOnly;
    }

    const canEditAssignment = backendCanEditAssignment !== null
      ? backendCanEditAssignment && !isDataReadOnly
      : false;
    const isAssignmentOnlyEditMode = canEditAssignment && !canEdit;

    // Use backend's granular permissions if available, otherwise fallback to old logic
    // State changes allowed for resolved (to close/reopen), blocked for closed/cancelled
    const canChangeState = backendCanChangeState !== null
      ? backendCanChangeState && !isFullyLocked
      : (hasUpdatePermission && !isFullyLocked); // Don't depend on canEdit

    // Notes and attachments are ALWAYS blocked for resolved/closed/cancelled states (no exceptions)
    // For non-final states: manager/admin/super_admin can always add, others need assignment or backend permission
    let canAddWorkNote = false;
    let canAddAttachments = false;

    console.log(`[TICKET PERMISSIONS DEBUG] ===== ATTACHMENTS DECISION =====`);
    if (isDataReadOnly) {
      console.log(`[TICKET PERMISSIONS DEBUG] BRANCH: isDataReadOnly - blocking attachments`);
      // Final states: NO ONE can add notes or attachments
      canAddWorkNote = false;
      canAddAttachments = false;
    } else {
      // Non-final states: check permissions
      if (isPrivilegedRole) {
        console.log(`[TICKET PERMISSIONS DEBUG] BRANCH: isPrivilegedRole - allowing attachments`);
        // Manager/admin/super_admin can always add notes and attachments
        canAddWorkNote = true;
        canAddAttachments = true;
      } else if (isAssignedToUser) {
        console.log(`[TICKET PERMISSIONS DEBUG] BRANCH: isAssignedToUser - allowing attachments`);
        // Ticket is assigned to logged-in user - they can add notes and attachments
        canAddWorkNote = true;
        canAddAttachments = true;
      } else {
        console.log(`[TICKET PERMISSIONS DEBUG] BRANCH: fallback to backend permissions`);
        console.log(`[TICKET PERMISSIONS DEBUG] backendCanAddNotes: ${backendCanAddNotes}`);
        console.log(`[TICKET PERMISSIONS DEBUG] backendCanAddAttachments: ${backendCanAddAttachments}`);
        console.log(`[TICKET PERMISSIONS DEBUG] hasUpdatePermission: ${hasUpdatePermission}`);
        // Not privileged and not assigned - check backend permissions
        canAddWorkNote = backendCanAddNotes !== null
          ? backendCanAddNotes
          : !isContact();
        canAddAttachments = backendCanAddAttachments !== null
          ? backendCanAddAttachments
          : hasUpdatePermission;
        console.log(`[TICKET PERMISSIONS DEBUG] Calculated canAddAttachments: ${canAddAttachments}`);
      }
    }
    console.log(`[TICKET PERMISSIONS DEBUG] Final canAddAttachments: ${canAddAttachments}`);

    // Related records blocked for resolved/closed/cancelled
    const canAddRelated = backendCanAddRelated !== null
      ? backendCanAddRelated && !isDataReadOnly
      : (hasUpdatePermission && !isDataReadOnly);

    // canPerformActions: for actions that create new records (duplicate, create service request, create change)
    // These actions are ALWAYS allowed for any user who can view the ticket
    // Since they create NEW records, they are not restricted by source ticket's assignment
    const canPerformActions = true;

    return {
      canView: true, // If user can access the ticket detail, they can view it
      canEdit,
      canEditAssignment,
      isAssignmentOnlyEditMode,
      canDelete: hasDeletePermission && !isFullyLocked,
      canChangeState,
      canAddWorkNote,
      canAddAttachments,
      canAddRelated,
      canPerformActions,
      editAccessReason: canEdit ? undefined : editAccessReason
    };
  }, [ticket, user, resource, can, isContact]);
}

/**
 * Helper function to determine if ticket data is read-only
 * Data cannot be edited when ticket is resolved, closed, or cancelled
 *
 * IMPORTANT: For type-specific tickets (CHANGE, PROBLEM), we prioritize the
 * type-specific state field (changeState, problemState) over the generic state.
 * However, if the generic state is 'closed' or 'cancelled' (e.g., from resource
 * cleanup service), the ticket is considered read-only regardless of the
 * type-specific state.
 */
function isTicketDataReadOnly(ticket: Ticket): boolean {
  const state = ticket.state?.toLowerCase();
  const changeState = ticket.changeState?.toLowerCase();
  const problemState = ticket.problemState?.toLowerCase();

  const readOnlyStates = ['resolved', 'closed', 'cancelled'];

  // If generic state is in terminal state (e.g., closed by resource cleanup),
  // treat as read-only regardless of type-specific state
  if (state && ['closed', 'cancelled'].includes(state)) {
    console.log(`[isTicketDataReadOnly] Generic state '${state}' is terminal - returning true`);
    return true;
  }

  // Check type-specific states
  if (changeState && readOnlyStates.includes(changeState)) {
    return true;
  }

  if (problemState && readOnlyStates.includes(problemState)) {
    return true;
  }

  // For non-Change/Problem tickets, check generic state
  if (!changeState && !problemState && state && readOnlyStates.includes(state)) {
    return true;
  }

  return false;
}

/**
 * Helper function to determine if a ticket is fully locked
 * State transitions are blocked only for closed and cancelled tickets
 * Resolved tickets can still be closed or reopened
 *
 * IMPORTANT: If the generic state is 'closed' or 'cancelled' (e.g., from
 * resource cleanup service), the ticket is considered fully locked regardless
 * of the type-specific state.
 */
function isTicketFullyLocked(ticket: Ticket): boolean {
  const state = ticket.state?.toLowerCase();
  const changeState = ticket.changeState?.toLowerCase();
  const problemState = ticket.problemState?.toLowerCase();

  const lockedStates = ['closed', 'cancelled'];

  // If generic state is locked (e.g., closed by resource cleanup),
  // treat as fully locked regardless of type-specific state
  if (state && lockedStates.includes(state)) {
    console.log(`[isTicketFullyLocked] Generic state '${state}' is locked - returning true`);
    return true;
  }

  // Check type-specific states
  if (changeState && lockedStates.includes(changeState)) {
    return true;
  }

  if (problemState && lockedStates.includes(problemState)) {
    return true;
  }

  return false;
}
